import React, { useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import {
  Box,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Switch,
  Grid,
  useMediaQuery,
  useTheme,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  SelectChangeEvent
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { EmployeeDetailsForm } from './EmployeeDetailsModal';

interface PayRates {
  basePayRate: string;
  basePayRateType: string;
  saturdayRate: string;
  saturdayRateType: string;
  sundayRate: string;
  sundayRateType: string;
  publicHolidayRate: string;
  publicHolidayRateType: string;
  additionalPublicHoliday: string;
  additionalPublicHolidayRateType: string;
  nightShiftRate: string;
  nightShiftRateType: string;
  nightShiftStartTime: string;
  nightShiftEndTime: string;
  holidays: Array<{ id: number; name: string; date: string; enabled: boolean }>;
}

type RateTypeField =
  | 'payRates.basePayRateType'
  | 'payRates.saturdayRateType'
  | 'payRates.sundayRateType'
  | 'payRates.publicHolidayRateType'
  | 'payRates.additionalPublicHolidayRateType'
  | 'payRates.nightShiftRateType';

const PayRatesTab: React.FC = () => {
  const {
    control,
    formState: { errors },
    watch,
    setValue
  } = useFormContext<EmployeeDetailsForm>();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [openHolidaysModal, setOpenHolidaysModal] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [newHoliday, setNewHoliday] = useState({
    name: '',
    date: new Date().toISOString().split('T')[0]
  });

  const rateTypeOptions = [
    { value: 'hour', label: 'Per Hour' },
    { value: 'day', label: 'Per Day' },
    { value: 'year', label: 'Per Annum' }
  ];

  const rateTypes = {
    basePayRate: watch('payRates.basePayRateType') || 'hour',
    saturdayRate: watch('payRates.saturdayRateType') || 'hour',
    sundayRate: watch('payRates.sundayRateType') || 'hour',
    publicHolidayRate: watch('payRates.publicHolidayRateType') || 'hour',
    additionalPublicHoliday: watch('payRates.additionalPublicHolidayRateType') || 'hour',
    nightShiftRate: watch('payRates.nightShiftRateType') || 'hour'
  };

  const holidays = watch('payRates.holidays') || [];
  const basePayRate = watch('payRates.basePayRate') || '';
  const basePayRateType = watch('payRates.basePayRateType') || 'hour';

  const handleHolidayToggle = (id: number) => {
    const updatedHolidays: Array<{ id: number; name: string; date: string; enabled: boolean }> =
      holidays.map((h: { id: number; name: string; date: string; enabled: boolean }) =>
        h.id === id ? { ...h, enabled: !h.enabled } : h
      );
    setValue('payRates.holidays', updatedHolidays, { shouldValidate: true });
  };

  const handleCreateHoliday = () => {
    if (newHoliday.name && newHoliday.date) {
      const id = holidays.length ? Math.max(...holidays.map((h: { id: number }) => h.id)) + 1 : 1;
      const updatedHolidays = [...holidays, { id, ...newHoliday, enabled: true }];
      setValue('payRates.holidays', updatedHolidays, { shouldValidate: true });
      setNewHoliday({ name: '', date: new Date().toISOString().split('T')[0] });
      setOpenCreateModal(false);
    }
  };

  const getRateAdornment = (field: keyof typeof rateTypes) => {
    switch (rateTypes[field]) {
      case 'hour':
        return '/hr';
      case 'day':
        return '/day';
      case 'year':
        return '/yr';
      default:
        return '/hr';
    }
  };

 const getEquivalentHourlyRate = () => {
   const rate = parseFloat(basePayRate);
   if (isNaN(rate) || rate === 0) return 0; // Return number instead of string

   switch (basePayRateType) {
     case 'hour':
       return rate;
     case 'day':
       return rate / 8; // Assuming 8-hour workday
     case 'year':
       return rate / 2080; // Assuming 40 hours/week * 52 weeks
     default:
       return 0;
   }
 };

const employment = watch('employment');
const baseHourlyRate = getEquivalentHourlyRate();
const leaveLoadingPercentage = Number(employment?.leaveLoading) || 0; // Convert to number and provide fallback

// Correct calculation: base rate + (base rate * leave loading percentage / 100)
const equivalentBasePayRate = baseHourlyRate * (1 + leaveLoadingPercentage / 100);

console.log(
  'Equivalent Base Pay Rate:',
  equivalentBasePayRate,
  baseHourlyRate,
  leaveLoadingPercentage,
  employment?.leaveLoading
);

  const renderRateTypeSelect = (name: RateTypeField) => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControl fullWidth size="small">
          <InputLabel>Rate Type</InputLabel>
          <Select
            {...field}
            label="Rate Type"
            onChange={(event: SelectChangeEvent<string>) => field.onChange(event.target.value)}
          >
            {rateTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    />
  );

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      {/* Base Pay Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          Base Pay Rate
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            {renderRateTypeSelect('payRates.basePayRateType')}
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Controller
              name="payRates.basePayRate"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Base pay rate"
                  type="number"
                  InputProps={{ endAdornment: getRateAdornment('basePayRate') }}
                  error={!!errors.payRates?.basePayRate}
                  helperText={errors.payRates?.basePayRate?.message || ''}
                  fullWidth
                  size="small"
                />
              )}
            />
          </Grid>
        </Grid>
        <Typography variant="body2" sx={{ mt: 2 }}>
          <Box component="span" sx={{ display: 'block', mb: 1 }}>
            Equivalent to{' '}
            <Box component="span" sx={{ fontWeight: 'bold' }}>
              ${equivalentBasePayRate}
            </Box>{' '}
            per hour
          </Box>
          <Box
            component="span"
            sx={{ display: 'block', color: 'text.secondary', fontSize: '0.875rem' }}
          >
            Rates will be applied on usual Monday to Friday
          </Box>
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Weekend Rates Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          Weekend Rates
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            {renderRateTypeSelect('payRates.saturdayRateType')}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="payRates.saturdayRate"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Saturday rate"
                  type="number"
                  InputProps={{ endAdornment: getRateAdornment('saturdayRate') }}
                  error={!!errors.payRates?.saturdayRate}
                  helperText={errors.payRates?.saturdayRate?.message || ''}
                  fullWidth
                  size="small"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            {renderRateTypeSelect('payRates.sundayRateType')}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="payRates.sundayRate"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Sunday rate"
                  type="number"
                  InputProps={{ endAdornment: getRateAdornment('sundayRate') }}
                  error={!!errors.payRates?.sundayRate}
                  helperText={errors.payRates?.sundayRate?.message || ''}
                  fullWidth
                  size="small"
                />
              )}
            />
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Holiday Rates Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          Holiday Rates
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            {renderRateTypeSelect('payRates.publicHolidayRateType')}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="payRates.publicHolidayRate"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Public holiday rate"
                  type="number"
                  InputProps={{ endAdornment: getRateAdornment('publicHolidayRate') }}
                  error={!!errors.payRates?.publicHolidayRate}
                  helperText={errors.payRates?.publicHolidayRate?.message || ''}
                  fullWidth
                  size="small"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            {renderRateTypeSelect('payRates.additionalPublicHolidayRateType')}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="payRates.additionalPublicHoliday"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Additional holiday rate"
                  type="number"
                  InputProps={{ endAdornment: getRateAdornment('additionalPublicHoliday') }}
                  error={!!errors.payRates?.additionalPublicHoliday}
                  helperText={errors.payRates?.additionalPublicHoliday?.message || ''}
                  fullWidth
                  size="small"
                />
              )}
            />
          </Grid>
        </Grid>

        <Button
          variant="text"
          onClick={() => setOpenHolidaysModal(true)}
          sx={{ mt: 2, textTransform: 'none' }}
          size="small"
        >
          Manage Public Holidays
        </Button>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Night Shift Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          Night Shift Rates
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            {renderRateTypeSelect('payRates.nightShiftRateType')}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="payRates.nightShiftRate"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Night shift rate"
                  type="number"
                  InputProps={{ endAdornment: getRateAdornment('nightShiftRate') }}
                  error={!!errors.payRates?.nightShiftRate}
                  helperText={errors.payRates?.nightShiftRate?.message || ''}
                  fullWidth
                  size="small"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <Controller
              name="payRates.nightShiftStartTime"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Start time"
                  type="time"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.payRates?.nightShiftStartTime}
                  helperText={errors.payRates?.nightShiftStartTime?.message || ''}
                  fullWidth
                  size="small"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <Controller
              name="payRates.nightShiftEndTime"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="End time"
                  type="time"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.payRates?.nightShiftEndTime}
                  helperText={errors.payRates?.nightShiftEndTime?.message || ''}
                  fullWidth
                  size="small"
                />
              )}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Variable Pay Rates Note */}
      <Box sx={{ mt: 3, p: 2, backgroundColor: theme.palette.grey[100], borderRadius: 1 }}>
        <Typography variant="body2">
          <strong>Note:</strong> These rates are applicable for timesheet and roster only. For
          regular pay runs, please use the template feature.
        </Typography>
      </Box>

      {/* Holidays Modal */}
      <Dialog
        open={openHolidaysModal}
        onClose={() => setOpenHolidaysModal(false)}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 1
          }}
        >
          Public Holidays
          <IconButton onClick={() => setOpenHolidaysModal(false)} size="small" edge="end">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Controller
            name="payRates.holidays"
            control={control}
            render={({ field }) => (
              <>
                {field.value.map(
                  (holiday: { id: number; name: string; date: string; enabled: boolean }) => (
                    <Box
                      key={holiday.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 1,
                        borderBottom: `1px solid ${theme.palette.divider}`
                      }}
                    >
                      <Box>
                        <Typography>{holiday.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(holiday.date).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </Typography>
                      </Box>
                      <Switch
                        checked={holiday.enabled}
                        onChange={() => handleHolidayToggle(holiday.id)}
                        color="primary"
                        size="small"
                      />
                    </Box>
                  )
                )}
              </>
            )}
          />
          <Button
            variant="contained"
            onClick={() => setOpenCreateModal(true)}
            sx={{ mt: 2 }}
            fullWidth={isMobile}
          >
            Add New Holiday
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenHolidaysModal(false)}>Cancel</Button>
          <Button onClick={() => setOpenHolidaysModal(false)} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Holiday Modal */}
      <Dialog
        open={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 1
          }}
        >
          Add New Holiday
          <IconButton onClick={() => setOpenCreateModal(false)} size="small" edge="end">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Date"
              type="date"
              value={newHoliday.date}
              onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="Holiday Name"
              value={newHoliday.name}
              onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
              fullWidth
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateModal(false)}>Cancel</Button>
          <Button onClick={handleCreateHoliday} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayRatesTab;
